import React, { useEffect, useMemo, useContext, useLayoutEffect } from "react";
import { MustacheTemplateRenderer } from "../../../atoms/components";
import { useState } from "react";
import {
  CasticulateNode,
  SlideContext,
} from "../../../pages/components/Casticulate";
import camelcaseKeys from "camelcase-keys";
import { getAssesmentPollTemplate } from "./template/assessmentPollTemplate";
import { marked } from "marked";

export interface CasticulateAssessmentPollProps {
  node?: CasticulateNode;
}

export function CasticulateAssessmentPoll({
  node,
}: CasticulateAssessmentPollProps) {
  const slideContext = useContext(SlideContext);
  const previousRecord = slideContext.slideStates.find(
    (slideState) => slideState.slideUuid === node?.id
  )?.assessmentSlideState;

  const nodeData = camelcaseKeys(node?.data, { deep: true });

  const [choiceUuid, setChoiceUuid] = useState(
    previousRecord?.answers?.[0] || ""
  );
  const [choiceText, setChoiceText] = useState(
    previousRecord?.answersInText?.[0] || ""
  );
  const [isCorrect, setIsCorrect] = useState(
    previousRecord?.isCorrect || false
  );
  const [hasAnswered, setHasAnswered] = useState(
    previousRecord?.hasAnswered || null
  );
  const [inputText, setInputText] = useState<string>("");

  const neutralFeedback = nodeData.neutralFeedbackMessage || "";
  const changedAnswer = previousRecord?.answers?.[0] !== choiceUuid;

  const ASSESSMENT_TEMPLATE_VIEW = useMemo(
    () => ({
      isGbf: /gbf/g.test(window.location.href),
      question: nodeData.question || "",
      choices: nodeData.choices || [],
      questionImage: () => {
        return !hasAnswered && nodeData.imageUri ? nodeData.imageUri : "hidden";
      },
      hasAnswered: hasAnswered,
      // showCorrectness: function () {
      //   return function (val: any, render: any) {
      //     const id = render(val);
      //     console.log(id);
      //     return choiceUuid === id ? "" : " !hidden ";
      //   };
      // },
      allChoiceHasImage: function () {
        return !nodeData?.choices.some(
          (choice: any) => choice?.imageUri === "" || choice?.image_uri === ""
        );
      },
      buttonText:
        hasAnswered && previousRecord?.answers?.includes(choiceUuid)
          ? node?.isEndNode
            ? "Finish"
            : "Continue"
          : "Answer to Continue",
      buttonId:
        hasAnswered && previousRecord?.answers?.includes(choiceUuid)
          ? node?.isEndNode
            ? "finishButton"
            : "nextButton"
          : "submitButton",
      disabled: choiceUuid === "" && inputText === "" ? "disabled" : "",
      disabledInput: previousRecord ? "disabled" : "",
      feedbackImage: () => {
        if (node?.slideType === "POLL") {
          return hasAnswered &&
            previousRecord?.answers?.includes(choiceUuid) &&
            nodeData.neutralFeedbackImage !== ""
            ? nodeData.neutralFeedbackImage
            : "hidden";
        }
      },
      progressBar: slideContext.progressBar,
      lastVisitedTitlePage: function () {
        return function (val: any, render: any) {
          const id = render(val);
          const progressBarItemTitle = slideContext.progressBar.find(
            (progressBarItem) =>
              progressBarItem.id === slideContext.lastVisitedTitlePageId
          )?.data.title;
          return id === slideContext.lastVisitedTitlePageId &&
            (progressBarItemTitle ? progressBarItemTitle.trim() !== "" : false)
            ? "pr-4"
            : "hidden";
        };
      },
      lastItemInProgressBar: function () {
        return function (val: any, render: any) {
          const id = render(val);
          return id ===
            slideContext.progressBar[slideContext.progressBar.length - 1].id
            ? "hidden"
            : "";
        };
      },
    }),
    [
      node,
      hasAnswered,
      choiceUuid,
      previousRecord,
      slideContext.lastVisitedTitlePageId,
      slideContext.progressBar,
    ]
  );

  function setChoice(this: HTMLElement) {
    const choiceUuid = this.getAttribute("value") || "";
    const choiceText = this.dataset.text || "";
    const isCorrect = this.dataset.isCorrect?.toLowerCase() === "true";
    setChoiceUuid(choiceUuid);
    setChoiceText(choiceText);
    setIsCorrect(isCorrect);
  }

  // Assessment Slide Use Effect
  useEffect(() => {
    // Get all button under an element with  id="choices"
    const buttons = document.querySelectorAll("#choices button");

    buttons.forEach((button) => {
      switch (node?.slideType) {
        case "POLL":
        case "MULTIPLE_CHOICE":
          button.addEventListener("click", setChoice);
      }
      button.addEventListener("click", setChoice);
      // Setting Colors for Selected and Not Selected Choices

      // If Button value matches with choice uuid, change color
      if (button.getAttribute("value") === choiceUuid) {
        button.classList.add("bg-white");
        button.classList.remove("text-white");
        button.classList.add("text-black");
      }
      // If Button value did not match with choice uuid, go back to default color
      else {
        button.classList.remove("bg-white");
        button.classList.remove("text-black");
        button.classList.add("text-white");
      }
    });

    return () => {
      buttons.forEach((button) => {
        button.removeEventListener("click", setChoice);
      });
    };
  }, [
    choiceUuid,
    node,
    ASSESSMENT_TEMPLATE_VIEW,
    slideContext.lastVisitedTitlePageId,
  ]);

  // Post Submissiong Use Effect
  useEffect(() => {
    const feedbackTextElement = document.getElementById("feedbackText");
    const feedbackIconElement = document.getElementById("feedbackIcon");
    const questionElement = document.getElementById("question");

    if (feedbackTextElement && questionElement && feedbackIconElement) {
      if (hasAnswered === true && node?.slideType === "POLL") {
        feedbackIconElement.innerHTML = "info";
        feedbackIconElement.classList.add("mr-2");

        feedbackTextElement.innerHTML = "Thank you!";
        questionElement.innerHTML =
          marked.parse(neutralFeedback) || neutralFeedback;
      }

      if (hasAnswered === true && changedAnswer) {
        feedbackIconElement.innerHTML = "";
        feedbackIconElement.classList.remove("mr-2");

        feedbackTextElement.innerHTML = nodeData.title;
        questionElement.innerHTML = nodeData.question;
      }

      if (hasAnswered === null) {
        feedbackIconElement.innerHTML = "";
        feedbackIconElement.classList.remove("mr-2");
        feedbackTextElement.innerHTML = nodeData.title;
        questionElement.innerHTML = nodeData.question;
      }
    }
  }, [previousRecord, isCorrect, choiceUuid, ASSESSMENT_TEMPLATE_VIEW]);

  const submitFunction = () => {
    setHasAnswered(true);
    slideContext.appendSlideStates({
      slideUuid: node?.id || "",
      assessmentSlideState: {
        activityType: node?.slideType || "",
        answers: [choiceUuid],
        answersInText: [choiceText],
        isCorrect: isCorrect,
        hasAnswered: true,
      },
    });
  };

  useLayoutEffect(() => {
    const required = true;
    const submitButton = document.getElementById("submitButton");
    submitButton?.addEventListener("click", submitFunction);

    slideContext.handleRerun();

    return () => {
      submitButton?.removeEventListener("click", submitFunction);
    };
  }, [ASSESSMENT_TEMPLATE_VIEW, choiceUuid]);

  return (
    <div className="box-border flex flex-col items-center h-full mx-auto">
      <MustacheTemplateRenderer
        template={getAssesmentPollTemplate(nodeData.theme)}
        view={ASSESSMENT_TEMPLATE_VIEW}
      />
    </div>
  );
}

// Assessment Template -----------------------------------------------------------------------------------------------------------------------------------------------------

// --------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
